import SlidingModal from "../Shared/SlidingModal.tsx";
import "../../assets/stylesheets/inputs.css";
import {FormEvent, useContext, useEffect, useRef, useState} from "react";
import {Gender, Patient, PatientFormData, PatientsContext} from "../../context/PatientsContext.tsx";
import {isValidDob} from "../../utils/dates.ts";
import {Optional} from "../../utils/types.ts";
import {useTranslation} from "react-i18next";

type AddPatientErrors = {
  firstName: boolean;
  lastName: boolean;
  dob: boolean;
  gender: boolean;
}

interface AddPatientModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  firstName?: Optional<string>;
  lastName?: Optional<string>;
  handleAssignPatient?: (patient: Patient) => void;
}

export default function AddPatientModal({open, setOpen, firstName, lastName, handleAssignPatient}: AddPatientModalProps) {
  
  const [errors, setErrors] = useState<AddPatientErrors>({firstName: false, lastName: false, dob: false, gender: false});
  const { handleAddPatient } = useContext(PatientsContext);
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  
  useEffect(() => {
    if(!open) formRef.current!.reset();
  }, [open]);
  
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const submissionErrors: AddPatientErrors = {
      firstName: !formData.get('first_name') || formData.get('first_name') === '',
      lastName: !formData.get('last_name') || formData.get('last_name') === '',
      dob: !!formData.get('dob') && !isValidDob(formData.get('dob') as string),
      gender: false,
    }
    setErrors(submissionErrors);
    if(Object.values(submissionErrors).some(e => e)) return;
    
    const body: PatientFormData = {
      firstName: formData.get('first_name') as string,
      lastName: formData.get('last_name') as string,
    }
    
    if(formData.get('dob')) body.dob = formData.get('dob') as string;
    if(formData.get('gender')) body.gender = formData.get('gender') as Gender;
    
    const patientCreated = await handleAddPatient(body);
    if(handleAssignPatient) {
      handleAssignPatient(patientCreated);
    }
    setOpen(false);
  }
  
  function handleFormatDate(e: FormEvent) {
    const input = e.target as HTMLInputElement;
    const value = input.value;
    if(value.length === 2 || value.length === 5) {
      input.value = value + '/';
    }
  }
  
  return (
    <SlidingModal open={open} setOpen={setOpen}>
      <section className={'flex flex-col gap-8 items-start justify-start px-6 py-10 relative h-full'}>
        <h4 className={'text-2xl font-bold text-[#363B43]'}>{t('patients.new')}</h4>
        <form ref={formRef} onSubmit={handleSubmit} className={'flex flex-col gap-6 items-start justify-start w-full'}>
          <div className={`flex flex-col items-start justify-start gap-3 w-full group ${errors.firstName ? 'errored' : ''}`}>
            <label htmlFor={'first-name'} className={'text-sm text-[#778C99] font-medium'}>{t('patients.firstName')}</label>
            <input required id={'first-name'} type={'text'} name={'first_name'} className={'group-[.errored]:outline-1 group-[.errored]:outline-red-400 text-[15px] bg-white w-full py-3 px-4 rounded-xl focus:outline-0 '} placeholder={'John'} defaultValue={firstName ? firstName : undefined}/>
          </div>
          <div className={`flex flex-col items-start justify-start gap-3 w-full group ${errors.lastName ? 'errored' : ''}`}>
            <label htmlFor={'last-name'} className={'text-sm text-[#778C99] font-medium'}>{t('patients.lastName')}</label>
            <input required id={'last-name'} type={'text'} name={'last_name'} className={'group-[.errored]:outline-1 group-[.errored]:outline-red-400 text-[15px] bg-white w-full py-3 px-4 rounded-xl focus:outline-0'} placeholder={'Doe'} defaultValue={lastName ? lastName : undefined}/>
          </div>
          <div className={`flex flex-col items-start justify-start gap-3 w-full group ${errors.dob ? 'errored' : ''}`}>
            <label htmlFor={'dob'} className={'text-sm text-[#778C99] font-medium'}>{t('patients.dateOfBirth')}</label>
            <input onInput={handleFormatDate} type={'text'} id={'dob'} name={'dob'} className={'group-[.errored]:outline-1 group-[.errored]:outline-red-400 text-[15px] bg-white w-full py-3 px-4 rounded-xl focus:outline-0'} pattern="(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\d\d" placeholder={'01/23/1990'}/>
            <span className={'font-medium text-xs text-red-400 hidden group-[.errored]:block'}>{t('forms.dateFormatError')}</span>
          </div>
          <div className={`flex flex-col items-start justify-start gap-3 w-full group ${errors.gender ? 'errored' : ''}`}>
            <label htmlFor={'gender'} className={'group-[.errored]:outline-1 group-[.errored]:outline-red-400 text-sm text-[#778C99] font-medium'}>{t('patients.gender')}</label>
            <select defaultValue={""} id={'gender'} name={'gender'} className={'text-[15px] bg-white w-full py-3 px-4 rounded-xl focus:outline-0 appearance-none inputs--custom-select'}>
              <option disabled value="" label={t('forms.selectOption')}>{t('forms.selectOption')}</option>
              <option value={'male'} label={t('patients.male')}>{t('patients.male')}</option>
              <option value={'female'} label={t('patients.female')}>{t('patients.female')}</option>
            </select>
          </div>
          
          <div className={'absolute bottom-10 left-6 w-[calc(100%-3rem)]'}>
            <button type={'submit'} className={'cursor-pointer hover:opacity-80 w-full text-center bg-[#26A27F] disabled:[bg-#B2E9D2] text-white font-semibold py-4 rounded-full'}>{t('common.save')}</button>
          </div>
        </form>
      </section>
    </SlidingModal>
  )
}